






































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { NOT_LIVE_REASONS, NotLiveReason } from "@/data/models/ClientAdministrationRow";
import { setClientLiveOperation } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { canSaveLiveOperation, hasLiveOperationChanged, notLiveReasonLabel } from "@/utils/ClientLiveOperation";
import { queueClientRowUpdate } from "@/utils/ClientListRowUpdates";

/**
 * Marks a client "Kein Echtbetrieb" with a reason (Test, Demo, Lager), or live operation again (ticket
 * 2610043). Mira leaves a marked client out from the next night on. IRES Cloud repeats every check and
 * records who changed it and when (`setClientLiveOperationForAdministration`).
 *
 * `show()` resolves with true once saved.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class LiveOperationDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  live = true;
  // null, not undefined: an undefined initializer is not reactive in vue-class-component.
  reason: NotLiveReason | null = null;
  initialReason: NotLiveReason | null = null;

  private resolve: ((saved: boolean) => void) | null = null;

  get reasonItems(): Array<{ value: NotLiveReason; label: string }> {
    return NOT_LIVE_REASONS.map((value) => ({
      value,
      label: notLiveReasonLabel(value, (key) => this.$t(key).toString()),
    }));
  }

  get hasChanged(): boolean {
    return hasLiveOperationChanged(this.initialReason, this.live, this.reason);
  }

  get canSave(): boolean {
    return canSaveLiveOperation(this.initialReason, this.live, this.reason);
  }

  /**
   * @param clientId - the client.
   * @param currentReason - its stored reason, null for live operation.
   */
  show(clientId: string, currentReason: NotLiveReason | null): Promise<boolean> {
    this.clientId = clientId;
    this.initialReason = currentReason;
    this.live = currentReason === null;
    this.reason = currentReason;
    this.baseDialog.show({
      title: this.$t("admin.clients.liveOperation.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 520,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(false);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(false);
    }
  }

  async onSave() {
    if (!this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const stored = await setClientLiveOperation(this.clientId, this.live ? null : this.reason);
      // The kept-alive Mandanten list shows the chip; it applies this on return instead of reloading.
      queueClientRowUpdate(this.clientId, { notLiveReason: stored && !stored.live ? stored.reason : null });
      SnackbarModule.showSuccess({ key: "admin.clients.liveOperation.saved" });
      this.close(true);
    } catch {
      // Cloud repeats the checks, so a refusal here is a stale screen or a lost connection.
      SnackbarModule.showError({ key: "admin.clients.liveOperation.saveFailed" });
    } finally {
      this.busy = false;
    }
  }

  close(saved: boolean) {
    this.baseDialog.isShowing = false;
    this.finish(saved);
  }

  private finish(saved: boolean) {
    this.resolve?.(saved);
    this.resolve = null;
  }
}
